import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./Blog.css";
import { blogPosts as staticBlogPosts } from "./PagesData/BlogPosts";
import { fetchBlogPosts } from "../api/blogApi";
import { isBlogAdmin } from "../utils/authClient";
import { useLocale } from "../context/LocaleContext";

function foldText(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function contentToPlainText(content) {
  if (!Array.isArray(content)) return "";
  const parts = [];
  for (const block of content) {
    if (!block || typeof block !== "object") continue;
    if (typeof block.text === "string") parts.push(block.text);
    if (block.type === "ul" && Array.isArray(block.items)) {
      parts.push(...block.items.map((it) => String(it)));
    }
    if (block.type === "img") {
      parts.push(String(block.alt || ""), String(block.src || ""));
    }
  }
  return parts.join(" ");
}

function postSearchHaystack(post) {
  const sf = Array.isArray(post.serviceFocus)
    ? post.serviceFocus.join(" ")
    : String(post.serviceFocus || "");
  return foldText(
    [
      post.title,
      post.excerpt,
      post.slug,
      post.seoTitle,
      post.seoDescription,
      sf,
      contentToPlainText(post.content),
    ].join(" ")
  );
}

function postMatchesQuery(post, rawQuery) {
  const q = String(rawQuery || "").trim();
  if (!q) return true;
  const hay = postSearchHaystack(post);
  const tokens = foldText(q)
    .split(/\s+/)
    .filter(Boolean);
  return tokens.every((t) => hay.includes(t));
}

function postMatchesLocale(post, locale) {
  const lang = post.locale === "en" ? "en" : "es";
  return lang === locale;
}

const Blog = () => {
  const { locale, t } = useLocale();
  const [allPosts, setAllPosts] = useState(staticBlogPosts);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const localePosts = useMemo(
    () => allPosts.filter((p) => postMatchesLocale(p, locale)),
    [allPosts, locale]
  );

  const filteredPosts = useMemo(
    () => localePosts.filter((p) => postMatchesQuery(p, search)),
    [localePosts, search]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchBlogPosts();
        if (!cancelled) {
          setAllPosts(data);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const resultsMeta = useMemo(() => {
    if (loading || !search.trim()) return null;
    const n = filteredPosts.length;
    if (n === 0) return t("blog.noResults");
    if (n === 1) return t("blog.resultsOne");
    return t("blog.resultsMany", { count: n });
  }, [loading, search, filteredPosts.length, t]);

  return (
    <div className="blog_page">
      <div className="blog_hero">
        <div className="blog_hero_inner">
          <h1 className="blog_title">{t("blog.title")}</h1>
          <p className="blog_subtitle">{t("blog.subtitle")}</p>
        </div>
      </div>

      <div className="blog_container">
        {loading ? <p className="blog_loading">{t("blog.loading")}</p> : null}
        <div className="blog_search_row">
          <label htmlFor="blog-search" className="blog_search_label">
            {t("blog.searchLabel")}
          </label>
          <div className="blog_search_wrap">
            <input
              id="blog-search"
              type="search"
              className="blog_search_input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("blog.searchPlaceholder")}
              autoComplete="off"
              spellCheck={false}
              aria-describedby="blog-search-hint"
            />
            {search.trim() ? (
              <button
                type="button"
                className="blog_search_clear"
                onClick={() => setSearch("")}
                aria-label={t("blog.clearSearch")}
              >
                ×
              </button>
            ) : null}
          </div>
          <p id="blog-search-hint" className="blog_search_hint">
            {t("blog.searchHint")}
          </p>
          {resultsMeta ? (
            <p className="blog_search_meta" role="status">
              {resultsMeta}
            </p>
          ) : null}
        </div>
        <div className="blog_grid">
          {filteredPosts.map((post, idx) => (
            <article
              key={post.slug}
              className={`blog_card blog_card_theme_${post.coverTheme}`}
            >
              <Link
                to={`/blog/${post.slug}`}
                className="blog_card_media_link"
                aria-label={t("blog.viewPostAria", { title: post.title })}
              >
                <div className="blog_card_media">
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    className="blog_card_img"
                    width={942}
                    height={721}
                    loading={idx === 0 ? "eager" : "lazy"}
                    fetchPriority={idx === 0 ? "high" : "low"}
                    decoding="async"
                  />
                </div>
              </Link>
              <div className="blog_card_body">
                <div className="blog_card_meta">
                  <span
                    className={`blog_lang ${post.locale === "en" ? "blog_lang_en" : "blog_lang_es"}`}
                  >
                    {post.locale === "en" ? "EN" : "ES"}
                  </span>
                  <span>{post.date}</span>
                  <span className="blog_dot">•</span>
                  <span>{post.readTime}</span>
                </div>
                <h2 className="blog_card_title">{post.title}</h2>
                <p className="blog_card_excerpt">{post.excerpt}</p>
                <div className="blog_card_actions">
                  <Link className="blog_read_more" to={`/blog/${post.slug}`}>
                    {t("blog.readMore")}
                  </Link>
                  {isBlogAdmin() ? (
                    <Link
                      className="blog_edit_post"
                      to={`/admin/blog/edit/${encodeURIComponent(post.slug)}`}
                    >
                      {t("blog.editPost")}
                    </Link>
                  ) : null}
                </div>
              </div>
            </article>
          ))}
        </div>
        {!loading && filteredPosts.length === 0 && !search.trim() ? (
          <p className="blog_loading">{t("blog.noArticles")}</p>
        ) : null}
      </div>
    </div>
  );
};

export default Blog;
